export default function ApproachSection() {
  return (
    <section className="relative py-24 md:py-32 px-6 md:px-10 bg-background-50">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="font-label text-sm tracking-widest uppercase text-secondary-600 mb-4">Approach</p>
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-900 leading-tight">
              A modern engineering philosophy
            </h2>
            <div className="w-12 h-px bg-accent-400 mt-6"></div>
          </div>
          <div className="lg:col-span-7">
            <div className="space-y-10">
              <div className="flex gap-5">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center mt-0.5">
                  <span className="font-label text-sm text-accent-700 font-semibold">01</span>
                </div>
                <div>
                  <h3 className="font-heading text-xl text-foreground-900 mb-2">Modern Development Workflows</h3>
                  <p className="font-body text-foreground-600 leading-relaxed">The company continuously refines its development processes, adopting automation, continuous integration, and collaborative practices that reduce friction and accelerate delivery without compromising quality. Each workflow is designed to catch issues early and maintain momentum throughout the development lifecycle.</p>
                </div>
              </div>
              <div className="flex gap-5">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mt-0.5">
                  <span className="font-label text-sm text-primary-700 font-semibold">02</span>
                </div>
                <div>
                  <h3 className="font-heading text-xl text-foreground-900 mb-2">Technology Exploration</h3>
                  <p className="font-body text-foreground-600 leading-relaxed">Staying current with technological evolution is not optional. The team actively evaluates new frameworks, languages, and paradigms, integrating those that demonstrably improve system quality and team productivity while maintaining stability for production environments.</p>
                </div>
              </div>
              <div className="flex gap-5">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center mt-0.5">
                  <span className="font-label text-sm text-secondary-700 font-semibold">03</span>
                </div>
                <div>
                  <h3 className="font-heading text-xl text-foreground-900 mb-2">Long-Term Technical Quality</h3>
                  <p className="font-body text-foreground-600 leading-relaxed">Short-term gains are never pursued at the expense of long-term integrity. Every technical decision is evaluated against its impact on future maintainability, extensibility, and the overall health of the system. Documentation, testing, and code review are treated as first-class engineering activities.</p>
                </div>
              </div>
              <div className="flex gap-5">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center mt-0.5">
                  <span className="font-label text-sm text-accent-700 font-semibold">04</span>
                </div>
                <div>
                  <h3 className="font-heading text-xl text-foreground-900 mb-2">Reliable &amp; Future-Ready</h3>
                  <p className="font-body text-foreground-600 leading-relaxed">Solutions are engineered to withstand the test of time. The team anticipates technological shifts and designs systems with adaptability in mind, ensuring that today's investments continue to deliver value well into the future without requiring disruptive rewrites.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto mt-24 md:mt-32">
        <div className="relative rounded-lg overflow-hidden h-[320px] md:h-[400px]">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20minimalist%20architectural%20composition%20with%20clean%20geometric%20planes%20intersecting%20at%20precise%20angles%20warm%20cream%20terracotta%20and%20soft%20golden%20amber%20light%20diffused%20through%20translucent%20surfaces%20creating%20depth%20and%20sophisticated%20visual%20harmony%20no%20text%20no%20people%20calm%20atmospheric%20editorial%20quality&width=1400&height=600&seq=approach-bg-filoxenia&orientation=landscape"
            alt="Abstract architectural composition with warm tones"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="px-8 md:px-14 max-w-lg">
              <p className="font-heading text-2xl md:text-3xl text-background-50 leading-snug">
                Precision in every layer of the stack
              </p>
              <p className="font-body text-sm text-background-200/80 mt-4 leading-relaxed">
                From system architecture to deployment pipelines, every detail is intentionally designed and rigorously validated.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}